/**
 * `signalGroupValue(row, key)` — QA batch 3 unit G2 (D-C, client half; `now`
 * dropped in fix-plan-batch3.md D-E — `localHourGroupLabel` no longer needs
 * a clock, it reads a precomputed, UTC-fixed 24-entry table).
 * Pure sibling to `signalsTableConfig.tsx`'s `groupValue` hook: FlowTable
 * routes every group header/count/collapse-key computation through this
 * function (`FlowTableConfig.groupValue`, `types.ts`), so the label AND the
 * group IDENTITY change together for every groupable Signals key, not just
 * `signal_tactic`/`step_number` (batch 1 D-E / batch 2 D-J's original,
 * narrower scope).
 *
 * The rule, per `src/shared/flow/signal-group-labels.ts` (D-C, landed G1;
 * D-F extends the map to every key the Group-by picker offers): a blank
 * value (`isBlank`) maps to that key's `SIGNAL_GROUP_EMPTY_LABELS` entry,
 * falling back to the raw value for a key with no entry (there are none
 * among the groupable columns after this batch, but `groupValue` must
 * still behave sanely for a key outside the map). Four keys need a
 * real-value transform on top of that blank rule:
 * - `step_number` — a real number renders `Step ${n}` (`stepNumberGroupLabel`).
 * - `flow_name` — resolved through `resolveFlowName` FIRST (an SFDC-id-shaped
 *   name becomes the non-blank `Unresolved Flow` label), then the blank rule
 *   applies to the resolved value.
 * - `local_time` — not a real column value at all; reads `row.local_time.hour`
 *   (the client-computed `LocalTimeResult`, `localTime.ts`) and renders the
 *   HOUR bucket (`localHourGroupLabel`), never Action View's minute-exact
 *   `getLocalTime` label (D-B, OQ1).
 * - `business_hours` — a group-only key (D-D, no column at all): reads
 *   `row.local_time.status` directly, no blank rule (a `LocalTimeResult`
 *   always has a status, `'unknown'` at worst).
 */
import {
  SIGNAL_GROUP_EMPTY_LABELS,
  BUSINESS_HOURS_GROUP_LABELS,
  resolveFlowName,
  stepNumberGroupLabel,
  localHourGroupLabel,
} from '@shared/flow/signal-group-labels';
import type { SignalRowWithComputed } from './signalsTableConfig';

/** `null`, `undefined`, or an all-whitespace string — "no real value" for both `groupValue` (D-E/D-C) and any renderer that needs the same test (`signalsTableConfig.tsx`'s `contact_location`). */
export function isBlank(v: unknown): boolean {
  return v == null || (typeof v === 'string' && v.trim() === '');
}

export function signalGroupValue(row: SignalRowWithComputed, key: string): unknown {
  if (key === 'local_time') {
    const hour = row.local_time?.hour;
    return typeof hour === 'number' ? localHourGroupLabel(hour) : SIGNAL_GROUP_EMPTY_LABELS.local_time;
  }
  if (key === 'business_hours') {
    return BUSINESS_HOURS_GROUP_LABELS[row.local_time.status];
  }

  let value = row[key];
  if (key === 'flow_name') {
    value = resolveFlowName(value as string | null | undefined);
  }
  if (isBlank(value)) {
    return SIGNAL_GROUP_EMPTY_LABELS[key] ?? value;
  }
  if (key === 'step_number' && typeof value === 'number') {
    return stepNumberGroupLabel(value);
  }
  return value;
}
